import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Image, ImageIcon, X, Upload, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ImagePickerProps {
  label: string;
  description?: string;
  selectedImage?: string;
  onImageChange: (imageUrl: string | undefined) => void;
  imageLibrary: string[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showPreview?: boolean;
  compact?: boolean;
}

export function ImagePicker({
  label,
  description,
  selectedImage,
  onImageChange,
  imageLibrary,
  placeholder = "Choose from your image library",
  className,
  disabled = false,
  showPreview = true,
  compact = false
}: ImagePickerProps) {
  const [previewImage, setPreviewImage] = useState<string | undefined>(selectedImage);

  useEffect(() => {
    setPreviewImage(selectedImage);
  }, [selectedImage]);

  const handleImageSelect = (imageUrl: string) => {
    if (imageUrl === 'none') {
      setPreviewImage(undefined);
      onImageChange(undefined);
    } else {
      setPreviewImage(imageUrl);
      onImageChange(imageUrl);
    }
  };

  const hasLibraryImages = imageLibrary && imageLibrary.length > 0;

  if (compact) {
    return (
      <div className={cn("space-y-2", className)}>
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex items-center gap-3">
          {/* Preview */}
          {showPreview && (
            <div className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 flex-shrink-0">
              {previewImage ? (
                <img src={previewImage} alt="Selected" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-slate-400" />
                </div>
              )}
            </div>
          )}
          
          {/* Selector */}
          <div className="flex-1">
            {hasLibraryImages ? (
              <Select
                value={previewImage || 'none'}
                onValueChange={handleImageSelect}
                disabled={disabled}
              >
                <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80">
                  <SelectValue placeholder={placeholder} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <X className="w-4 h-4 text-slate-400" />
                      <span>No image</span>
                    </div>
                  </SelectItem>
                  {imageLibrary.map((imageUrl, index) => (
                    <SelectItem key={imageUrl} value={imageUrl}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded overflow-hidden border border-slate-200">
                          <img src={imageUrl} alt={`Option ${index + 1}`} className="w-full h-full object-cover" />
                        </div>
                        <span>Library Image {index + 1}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="p-3 border border-dashed border-slate-300 rounded-lg bg-slate-50">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Upload className="w-4 h-4" />
                  <span>No images in library yet</span>
                  <Button variant="link" size="sm" className="p-0 h-auto text-blue-600">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Add images
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={cn("border border-slate-200/60 shadow-sm", className)}>
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Header */}
          <div>
            <Label className="text-sm font-medium flex items-center gap-2">
              <Image className="w-4 h-4 text-slate-500" />
              {label}
            </Label>
            {description && (
              <p className="text-xs text-slate-600 mt-1">{description}</p>
            )}
          </div>

          {/* Content */}
          {hasLibraryImages ? (
            <div className="space-y-3">
              {/* Preview */}
              {showPreview && (
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                    {previewImage ? (
                      <img src={previewImage} alt="Selected" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900">
                      {previewImage ? 'Selected Image' : 'No Image Selected'}
                    </div>
                    <div className="text-xs text-slate-600">
                      {previewImage ? 'This image will be used for this item' : 'Choose from your library below'}
                    </div>
                  </div>
                </div>
              )}

              {/* Selector */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-slate-700">Choose from Library</Label>
                <Select
                  value={previewImage || 'none'}
                  onValueChange={handleImageSelect}
                  disabled={disabled}
                >
                  <SelectTrigger className="focus:ring-primary focus:border-primary border-slate-200/80">
                    <SelectValue placeholder={placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <div className="flex items-center gap-2">
                        <X className="w-4 h-4 text-slate-400" />
                        <span>No image (use default)</span>
                      </div>
                    </SelectItem>
                    {imageLibrary.map((imageUrl, index) => (
                      <SelectItem key={imageUrl} value={imageUrl}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded overflow-hidden border border-slate-200">
                            <img src={imageUrl} alt={`Library ${index + 1}`} className="w-full h-full object-cover" />
                          </div>
                          <span>Library Image {index + 1}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {previewImage && (
                <div className="flex items-center justify-between p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                      Selected
                    </Badge>
                    <span>Image assigned successfully</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleImageSelect('none')}
                    className="text-emerald-600 hover:text-emerald-700 h-6 px-2"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-3" />
              <div className="text-sm font-medium text-slate-700 mb-1">
                No Images in Library
              </div>
              <div className="text-xs text-slate-600 mb-3">
                Add images to your library first to assign them to activities
              </div>
              <Button variant="outline" size="sm" disabled={disabled}>
                <ExternalLink className="w-3 h-3 mr-1" />
                Go to Images Settings
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
